import React, { useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';

import { CommonComponentProps, ImageObject } from '../pages/types';
import ImageEditorTooltip from './Editor/Components/ImageEditorTooltip';
import ImageFullscreen from './image-fullscreen';

interface ImageGalleryProps extends CommonComponentProps {
  images: ImageObject[];
  rowHeight: string | number;
  columns?: number; // Number of columns on desktop
  minColumns?: number; // Minimum columns on mobile (overrides responsive behavior)
  gap?: string; // Gap between images
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  id,
  images,
  rowHeight,
  columns = 3, // Default to 3 columns
  minColumns, // No default - optional override
  gap = '8px', // Default gap
  sx,
  isEditable,
  onPropsChange,
  onImageUpload,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  const handleImageClick = (index: number) => {
    if (isEditable && onImageUpload && onPropsChange) {
      onImageUpload((result) => {
        const url = Array.isArray(result) ? result[0] : result;
        const newImages = [...images];
        newImages[index] = { ...newImages[index], url: url };
        onPropsChange({ images: newImages });
      });
    }
  };

  const handleAddImage = () => {
    if (isEditable && onImageUpload && onPropsChange) {
      onImageUpload(
        (result) => {
          const urls = Array.isArray(result) ? result : [result];
          const newImages = [
            ...images,
            ...urls.map((url) => ({ url, title: '', altText: '' })),
          ];
          onPropsChange({ images: newImages });
        },
        { multiple: true },
      );
    }
  };

  // Calculate responsive columns
  const tabletColumns = Math.max(1, Math.floor(columns / 2)); // Half of desktop columns
  const mobileColumns = minColumns || 1; // Use minColumns if specified, otherwise 1

  // Preload images in order to prevent flickering
  useEffect(() => {
    // Reset loaded images when images prop changes
    setLoadedImages(new Set());

    // Preload images sequentially
    const preloadImages = async () => {
      for (let i = 0; i < images.length; i++) {
        const img = new Image();
        img.src = images[i].url || '';

        try {
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              setLoadedImages((prev) => {
                const newSet = new Set(prev);
                newSet.add(i);
                return newSet;
              });
              resolve();
            };
            img.onerror = () => {
              // Even on error, mark as "loaded" so it doesn't block others
              setLoadedImages((prev) => {
                const newSet = new Set(prev);
                newSet.add(i);
                return newSet;
              });
              resolve();
            };
            // Timeout after 5 seconds to prevent blocking
            setTimeout(() => {
              setLoadedImages((prev) => {
                const newSet = new Set(prev);
                newSet.add(i);
                return newSet;
              });
              resolve();
            }, 5000);
          });
        } catch (error) {
          console.error(`Failed to load image ${i}:`, error);
        }
      }
    };

    preloadImages();
  }, [images]);

  return (
    <Box
      id={id}
      sx={{
        py: '10px',
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(${mobileColumns}, 1fr)`, // 1 column on mobile
          sm: `repeat(${tabletColumns}, 1fr)`, // Half columns on tablet
          md: `repeat(${columns}, 1fr)`, // Full columns on desktop
        },
        gridAutoRows: rowHeight, // Set the height of the rows
        gap: gap, // Use configurable gap
        ...sx,
      }}
    >
      {images.map((image, index) => {
        const content = (
          <Box
            component="img"
            src={image.url || ''}
            alt={image.altText || `Image ${index + 1}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              aspectRatio: '9/9',
              cursor: isEditable ? 'pointer' : 'pointer',
              opacity: loadedImages.has(index) ? 1 : 0,
              transition: 'opacity 0.3s ease-in',
              '&:hover': isEditable
                ? {
                    outline: '2px dashed #1976d2',
                    zIndex: 1,
                  }
                : {},
            }}
            onClick={(e) => {
              // Click handler for non-editable mode only
              if (!isEditable) {
                setCurrentIndex(index);
              }
            }}
          />
        );

        return isEditable ? (
          <Box key={index} sx={{ width: '100%', height: '100%' }}>
            <ImageEditorTooltip
              image={image}
              onEdit={() => handleImageClick(index)}
              onDelete={() => {
                if (onPropsChange) {
                  const newImages = [...images];
                  newImages.splice(index, 1);
                  onPropsChange({ images: newImages });
                }
              }}
              onTextEdit={(title, altText) => {
                if (onPropsChange) {
                  const newImages = [...images];
                  newImages[index] = {
                    ...newImages[index],
                    title,
                    altText,
                  };
                  onPropsChange({ images: newImages });
                }
              }}
              onMoveBackward={
                index > 0
                  ? () => {
                      if (onPropsChange) {
                        const newImages = [...images];
                        const temp = newImages[index];
                        newImages[index] = newImages[index - 1];
                        newImages[index - 1] = temp;
                        onPropsChange({ images: newImages });
                      }
                    }
                  : undefined
              }
              onMoveToFirst={
                index > 0
                  ? () => {
                      if (onPropsChange) {
                        const newImages = [...images];
                        const imageToMove = newImages[index];
                        newImages.splice(index, 1);
                        newImages.unshift(imageToMove);
                        onPropsChange({ images: newImages });
                      }
                    }
                  : undefined
              }
              onMoveForward={
                index < images.length - 1
                  ? () => {
                      if (onPropsChange) {
                        const newImages = [...images];
                        const temp = newImages[index];
                        newImages[index] = newImages[index + 1];
                        newImages[index + 1] = temp;
                        onPropsChange({ images: newImages });
                      }
                    }
                  : undefined
              }
            >
              {content}
            </ImageEditorTooltip>
          </Box>
        ) : (
          <ImageFullscreen
            firstImage={image}
            images={images}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            key={index}
          >
            {content}
          </ImageFullscreen>
        );
      })}
      {isEditable && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.05)',
            cursor: 'pointer',
            border: '2px dashed #ccc',
            aspectRatio: '9/9', // Match aspect ratio of other images
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderColor: '#666',
            },
          }}
          onClick={handleAddImage}
        >
          <AddIcon sx={{ fontSize: 48, color: '#999' }} />
        </Box>
      )}
    </Box>
  );
};

export default ImageGallery;
